import React, { Fragment } from 'react';

import { useParams } from 'react-router-dom';

import { Text } from '@components/common';
import { Grid } from '@components/common/Grid';
import { DashboardLayout, DashboardLayoutBody } from '@components/layouts/DashboardLayout';
import { Portal } from '@components/Portal';
import { PageHeader } from '@components/shared/PageHeader';
import { CreateStudyNoTeamAlert } from '@components/StudiesApp/components/StudyNew/components/CreateStudyNoTeamAlert';
import { NewStudyButton } from '@components/StudiesApp/components/StudyNew/components/NewStudyButton';
import { STUDY_TYPE_KINDS_BY_KEY, STUDY_TYPE_OPTIONS } from '@components/StudiesApp/components/StudyNew/constants';
import { NewStudySpriteSVG } from '@components/svgs/NewStudySpriteSVG';
import { useFeature } from '@hooks/useFeature';
import { useTeams } from '@hooks/useTeams';
import { useUser } from '@hooks/useUser';
import { UserTeamsChannel, useWebsocket } from '@hooks/useWebsocket';
import { useGetWhoamiQuery } from '@stores/whoami';

import { AgentPrompt } from '../AgentPrompt';

const ColumnHeader = ({ children }: { children: React.ReactNode }) => (
  <Text bold h='600' className='mb-2'>
    {children}
  </Text>
);

export const StudyNew: React.FC<React.PropsWithChildren<unknown>> = () => {
  const { teamId: teamIdStr } = useParams<{ teamId: string }>();
  const teamId = teamIdStr ? parseInt(teamIdStr) : null;
  const { findTeam } = useTeams();
  const team: Team | undefined = teamId ? findTeam(teamId) : undefined;
  const teamAccessControlEnabled = useFeature('team_access_control');
  const hasChatPrototype = useFeature('chat_prototype');
  const { id: user_id } = useUser();
  const { data: whoami, isLoading, refetch } = useGetWhoamiQuery();

  const teams = whoami?.user?.teams || [];

  useWebsocket<{ user_id: number }, void>({
    channel: UserTeamsChannel,
    params: { user_id },
    onReceived: refetch
  });

  const showNeedsTeamError = teamAccessControlEnabled && !teams?.length;

  return (
    <DashboardLayout>
      <PageHeader hideDefaultButtons team={team} h1='Create new study' />

      {hasChatPrototype && (
        <div className='px-page py-4'>
          <Text h='600' mb='4' bold>
            Draft with AI
          </Text>
          <AgentPrompt />
        </div>
      )}

      <DashboardLayoutBody>
        {showNeedsTeamError && <CreateStudyNoTeamAlert />}
        {STUDY_TYPE_OPTIONS.map(({ key, label }) => (
          <Fragment key={key}>
            <ColumnHeader>{label}</ColumnHeader>
            <Grid
              className='monitor:mb-10 tablet:mb-6 mb-4 gap-x-6 gap-y-4'
              mobile={1}
              tablet={1}
              desktop={2}
              monitor={3}
              data-testid='grid'
            >
              {STUDY_TYPE_KINDS_BY_KEY[key]?.map((kind) => (
                <NewStudyButton kind={kind} teamId={teamId} key={kind} disabled={showNeedsTeamError || isLoading} />
              ))}
            </Grid>
          </Fragment>
        ))}
      </DashboardLayoutBody>
      <Portal>
        <NewStudySpriteSVG style={{ display: 'none' }} data-testid='new-study-sprite-svg' />
      </Portal>
    </DashboardLayout>
  );
};
